const { getOriginalImageUrl } = require('utils/image');

const internals = {};

internals.getCameraApi = () => {

    if (typeof navigator === 'undefined') {
        return null;
    }

    return navigator.camera || null;
};

internals.hasNativeCamera = () => {

    const camera = internals.getCameraApi();

    return !!(camera && typeof camera.getPicture === 'function');
};

internals.isMobileCapturePreferred = () => {

    if (typeof window === 'undefined') {
        return false;
    }

    if (window.cordova) {
        return true;
    }

    const userAgent = navigator.userAgent || '';

    return /iPhone|iPad|iPod|Android/i.test(userAgent);
};

internals.normalizeCameraDataUrl = (imageData) => {

    if (!imageData) {
        return '';
    }

    if (String(imageData).startsWith('data:')) {
        return imageData;
    }

    return `data:image/jpeg;base64,${imageData}`;
};

internals.fileFromDataUrl = (dataUrl) => {

    const source = String(dataUrl || '');
    const commaIndex = source.indexOf(',');
    const hasPayload = commaIndex !== -1;
    const header = hasPayload ? source.slice(0, commaIndex) : '';
    const payload = hasPayload ? source.slice(commaIndex + 1) : source;
    const mimeMatch = header.match(/data:([^;]+)/);
    const type = (mimeMatch && mimeMatch[1]) || 'image/jpeg';
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    const extension = type === 'image/png' ? 'png' : 'jpg';

    return new File([bytes], `camera-photo.${extension}`, { type });
};

internals.isCameraCancel = (error) => {

    const message = String(error || '').toLowerCase();

    return message.includes('cancel') || message.includes('no image selected');
};

internals.getCameraConstants = () => {

    const Camera = (typeof window !== 'undefined' && window.Camera) || {};

    return {
        destinationType: Camera.DestinationType || {},
        pictureSourceType: Camera.PictureSourceType || {},
        encodingType: Camera.EncodingType || {},
        mediaType: Camera.MediaType || {},
        direction: Camera.Direction || {}
    };
};

internals.captureWithCordovaCamera = ({ onPhoto }) => {

    const camera = internals.getCameraApi();
    const {
        destinationType,
        pictureSourceType,
        encodingType,
        mediaType,
        direction
    } = internals.getCameraConstants();

    return new Promise((resolve, reject) => {

        camera.getPicture(
            (imageData) => {

                try {
                    const file = internals.fileFromDataUrl(internals.normalizeCameraDataUrl(imageData));
                    onPhoto(file);
                    resolve();
                }
                catch (err) {
                    reject(err);
                }
            },
            (error) => {

                if (internals.isCameraCancel(error)) {
                    resolve();
                    return;
                }

                reject(error instanceof Error ? error : new Error(String(error)));
            },
            {
                quality: 80,
                destinationType: destinationType.DATA_URL ?? 0,
                sourceType: pictureSourceType.CAMERA ?? 1,
                encodingType: encodingType.JPEG ?? 0,
                mediaType: mediaType.PICTURE ?? 0,
                cameraDirection: direction.FRONT ?? 1,
                correctOrientation: true,
                saveToPhotoAlbum: false,
                allowEdit: false,
                targetWidth: 1600,
                targetHeight: 1600
            }
        );
    });
};

internals.captureWithFileInput = ({ onPhoto }) => {

    return new Promise((resolve) => {

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.setAttribute('capture', 'user');
        input.style.display = 'none';

        const cleanup = () => {

            input.remove();
        };

        input.addEventListener('change', () => {

            const file = input.files && input.files[0];
            cleanup();

            if (file) {
                onPhoto(file);
            }

            resolve();
        });

        document.body.appendChild(input);
        input.click();
    });
};

internals.captureWithGetUserMedia = async ({
    cameraButtonStyle,
    onPhoto
}) => {

    const stream = await navigator.mediaDevices.getUserMedia({
        video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
        }
    });

    const video = document.createElement('video');
    video.srcObject = stream;
    video.autoplay = true;
    video.style.transform = 'scaleX(-1)';

    const dialog = document.createElement('dialog');
    dialog.style.padding = '20px';

    const takePhotoBtn = document.createElement('button');
    takePhotoBtn.textContent = 'Take Photo';
    takePhotoBtn.className = 'primary';
    takePhotoBtn.style.cssText = cameraButtonStyle;

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.className = 'secondary';
    cancelBtn.style.cssText = cameraButtonStyle;

    const cleanup = () => {

        stream.getTracks().forEach((track) => track.stop());
        dialog.remove();
    };

    dialog.appendChild(video);
    dialog.appendChild(document.createElement('br'));
    dialog.appendChild(takePhotoBtn);
    dialog.appendChild(cancelBtn);

    document.body.appendChild(dialog);
    dialog.showModal();

    takePhotoBtn.onclick = () => {

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');

        ctx.scale(-1, 1);
        ctx.translate(-canvas.width, 0);

        ctx.drawImage(video, 0, 0);

        canvas.toBlob((blob) => {

            const file = new File([blob], 'webcam-photo.jpg', { type: 'image/jpeg' });
            onPhoto(file);
            cleanup();
        }, 'image/jpeg');
    };

    cancelBtn.onclick = cleanup;
    dialog.addEventListener('close', cleanup);
};

exports.captureProfilePhoto = async ({
    cameraButtonStyle,
    onPhoto
}) => {

    if (internals.hasNativeCamera()) {
        return internals.captureWithCordovaCamera({ onPhoto });
    }

    if (internals.isMobileCapturePreferred()) {
        return internals.captureWithFileInput({ onPhoto });
    }

    return internals.captureWithGetUserMedia({
        cameraButtonStyle,
        onPhoto
    });
};

exports.getProfilePhotoCaptureErrorMessage = () => {

    if (typeof window !== 'undefined' && window.cordova) {
        return 'Unable to access camera. Check that camera permission is enabled for Nearpeer.';
    }

    if (internals.isMobileCapturePreferred()) {
        return 'Unable to access camera. Please allow camera access and try again.';
    }

    return 'Unable to access camera. Please click the icon to the immediate left of the URL in the address bar.';
};

exports.fetchOriginalProfilePhotoFile = async ({
    http,
    originalPicture,
    fileName = 'tmp.jpeg'
}) => {

    const url = getOriginalImageUrl(originalPicture);
    const response = await http.get(url, { responseType: 'blob' });

    return new File([response.data], fileName);
};

exports.validateProfilePhotoFile = (file, {
    maxSizeMb = 9.9,
    mbInBytes = 1000000,
    acceptedFiletypes = ['bmp', 'tif', 'tiff', 'gif', 'jpeg', 'jpg', 'jif', 'jfif', 'jp2', 'jpx', 'j2k', 'j2c', 'fpx', 'pcd', 'png', 'webp']
} = {}) => {

    if (!file) {
        return null;
    }

    const extensionRejectionMsg = 'File must have one of these extensions: ' + acceptedFiletypes.join(', ');
    const match = file.name.split('.').pop();

    if (!match) {
        return extensionRejectionMsg;
    }

    const ext = match.toLowerCase();

    if (!acceptedFiletypes.includes(ext)) {
        return extensionRejectionMsg;
    }

    if (file.size > (maxSizeMb * mbInBytes)) {
        return 'File must be under 10mb';
    }

    return null;
};

exports.internals = internals;
